import { Column } from '../../../types/WorkspaceViewColumn';
import { WorkspaceFramework } from '../../../types/Workspace';
import { WorkspaceViewEntityType, WorkspaceViewGroup } from '../../../types/WorkspaceView';

/**
 * Single source of truth for which table columns render, shared by StickyColumnHeader
 * and VirtualizedTaskRow. The two used to decide independently, so any rule that only
 * one of them knew about (notably the epic column set) misaligned the header against
 * the body. (#2544)
 *
 * Keep in sync with `calculateNonTitleWidth` (utils/taskListUtils.ts), which reserves
 * the pixel width of exactly these columns.
 */
export type ColumnVisibilityContext = {
  hiddenColumns: Column[];
  viewEntityType?: WorkspaceViewEntityType;
  viewGroup?: WorkspaceViewGroup;
  framework?: WorkspaceFramework;
};

/** Columns that carry no meaning for an epic. Dependencies are tracked between tasks, not epics. */
const EPIC_HIDDEN_COLUMNS: Column[] = [
  Column.sprintStatus,
  Column.epic,
  Column.status,
  Column.priority,
  Column.estimate,
  Column.label,
  Column.blocking,
  Column.blockedBy,
];

export const isColumnVisible = (column: Column, ctx: ColumnVisibilityContext): boolean => {
  const { hiddenColumns, viewEntityType, viewGroup, framework } = ctx;

  if (hiddenColumns.includes(column)) return false;

  // Subitems aren't allowed in triage, so the column is meaningless there.
  if (column === Column.subitems && viewGroup === WorkspaceViewGroup.triage) return false;

  if (column === Column.sprintStatus) {
    // Triage and Archive describe a task's stage rather than its sprint, so the column
    // stays useful there even when the space has no sprint framework.
    const isStageContextual = viewGroup === WorkspaceViewGroup.triage || viewGroup === WorkspaceViewGroup.archive;
    if (
      (framework === WorkspaceFramework.none && !isStageContextual) ||
      viewGroup === WorkspaceViewGroup.sprint ||
      viewGroup === WorkspaceViewGroup.kanban
    ) {
      return false;
    }
  }

  if (viewEntityType === WorkspaceViewEntityType.epic && EPIC_HIDDEN_COLUMNS.includes(column)) {
    return false;
  }

  return true;
};

/**
 * The 20px chevron affordance in front of the title. Epics have no subitems to expand,
 * and triage hides subitems entirely.
 */
export const isExpandColumnVisible = (ctx: ColumnVisibilityContext): boolean =>
  ctx.viewEntityType !== WorkspaceViewEntityType.epic &&
  !ctx.hiddenColumns.includes(Column.subitems) &&
  ctx.viewGroup !== WorkspaceViewGroup.triage;
